<template>
  <div class="page-content || payment">
    <div class="payment-header">
      <CustomIcon name="logo" class="payment-header-logo" />
      <h2 class="payment-title">
        {{ getTitle() }}
      </h2>
    </div>
    <div class="icon-text-set">
      <IconText iconName="vr" text="Personalized VR exposure therapy plan" />
      <IconText
        iconName="program"
        text="Develop skills through clinical psychoeducation"
      />
    </div>
    <h3 class="payment-subtitle">Set up your account</h3>
    <FieldInput
      name="email"
      placeholder="Email"
      :value="formData.email.value"
      :touched="formData.email.touched"
      :onBlur="() => updateForm('email', formData.email.value, true)"
      :onChange="(event) => updateForm('email', event.target.value, false)"
      :validator="(value) => validator['email'](value)"
    />
    <p class="payment-text">
      oVRcome will use this email to authenticate logins. Ensure you have access
      to it on your mobile phone.
    </p>
    <h3 class="payment-subtitle">Your VR headset</h3>
    <div class="payment-image">
      <img
        :src="require('@/assets/images/ovrcome_vr_headset.png')"
        name="ovrcome-headset"
        alt="ovrcome-headset-image"
      />
    </div>
    <h3 class="payment-subtitle">Delivery address</h3>
    <FieldInput
      name="name"
      placeholder="e.g. Bob Smith"
      labelText="Full Name"
      :value="formData.name.value"
      :touched="formData.name.touched"
      :onBlur="() => updateForm('name', formData.name.value, true)"
      :onChange="(event) => updateForm('name', event.target.value, false)"
      :validator="(value) => validator['name'](value)"
    />
    <div class="field">
      <label for="street-address" class="field-label">Street Address</label>
      <input
        tabindex="0"
        ref="streetAddressLookUp"
        class="field-input"
        type="text"
        id="street-address"
        placeholder="e.g. Building number and street name"
        :value="formData.address.value"
        :touched="formData.address.touched"
        @focus="() => updateForm('address', formData.address.value, true)"
        @blur="() => updateForm('address', formData.address.value, true)"
        @change="(event) => updateForm('address', event.currentTarget.value)"
        @input.once="autoCompleteService()"
      />
    </div>
    <FieldInput
      name="apartment"
      labelText="Address Line 2 (optional)"
      placeholder="e.g. Unit/Suite/Apt number"
      :value="formData.apartment.value"
      :touched="formData.apartment.touched"
      :onBlur="() => updateForm('apartment', formData.apartment.value, true)"
      :onChange="(event) => updateForm('apartment', event.target.value, false)"
      :validator="(value) => validator['apartment'](value)"
    />
    <FieldInput
      name="locality"
      placeholder="e.g. Dallas"
      labelText="City"
      :value="formData.locality.value"
      :touched="formData.locality.touched"
      :onBlur="() => updateForm('locality', formData.locality.value, true)"
      :onChange="(event) => updateForm('locality', event.target.value, false)"
      :validator="(value) => validator['locality'](value)"
    />
    <FieldSelector
      labelText="Country"
      :options="countries"
      placeholder="Select Country"
      :value="formData.countryAlpha2.value"
      :onChange="updateCountryField"
      :validator="(value) => validator['country'](value)"
    />
    <FieldSelector
      :disabled="!formData.countryId.value"
      placeholder="e.g. Texas"
      name="state-selector"
      type="selector"
      :autoComplete="false"
      labelText="State/Province"
      :options="states"
      :value="formData.stateId.value"
      :touched="formData.stateId.touched"
      :onBlur="() => updateForm('stateId', formData.stateId.value, true)"
      :onChange="(event) => updateForm('stateId', event.target.value, false)"
    />
    <FieldInput
      name="postal"
      labelText="Zipcode/Postcode"
      placeholder="e.g. 73301"
      :value="formData.postal.value"
      :touched="formData.postal.touched"
      :onBlur="() => updateForm('postal', formData.postal.value, true)"
      :onChange="(event) => updateForm('postal', event.target.value, false)"
      :validator="(value) => validator['postal'](value)"
    />
    <FieldInput
      name="phoneNumber"
      labelText="Phone number (Required for shipping)"
      placeholder="e.g. +1 555 555 5555"
      :value="formData.phoneNumber.value"
      :touched="formData.phoneNumber.touched"
      :onBlur="
        () => updateForm('phoneNumber', formData.phoneNumber.value, true)
      "
      :onChange="
        (event) => updateForm('phoneNumber', event.target.value, false)
      "
      :validator="(value) => validator['phoneNumber'](value)"
    />
    <p class="payment-text">
      We currently ship from the US and New Zealand. For overseas deliveries
      international shipping costs and timeframes will apply, as well as duty
      (where applicable).
    </p>
    <SnackbarPrompt icon="done" variation="success">
      All costs are covered, you will not be charged
    </SnackbarPrompt>
    <SnackbarPrompt v-if="warning" icon="warning" variation="warning">{{
      warning
    }}</SnackbarPrompt>
    <CustomButton
      :disabled="!formIsValid() || isLoading"
      :onClick="submitFormData"
    >
      <span v-if="!isLoading">Confirm</span>
      <LoadingIndicator v-else
    /></CustomButton>
  </div>
</template>
<script>
import FieldInput from "@/components/FieldInput.vue";
import FieldSelector from "@/components/FieldSelector.vue";
import CustomIcon from "@/components/CustomIcon.vue";
import IconText from "@/components/IconText.vue";
import CustomButton from "@/components/CustomButton.vue";
import LoadingIndicator from "@/components/LoadingIndicator.vue";
import SnackbarPrompt from "@/components/SnackbarPrompt.vue";
import GooglePlaces from "@/services/google-places";
import { PAYMENT_FORMS, MODULES } from "@/config";
import {
  fillInAddress,
  partnerPaymentValidation,
  getCategoryById,
} from "@/utils";
import { mapGetters } from "vuex";
import * as queryString from "query-string";

export default {
  data() {
    return {
      phobia: null,
      validator: partnerPaymentValidation,
    };
  },
  props: {
    updateFormState: Function,
    submitFormData: Function,
    formData: Object,
    isLoading: Boolean,
    warning: String,
  },
  components: {
    FieldInput,
    FieldSelector,
    CustomIcon,
    CustomButton,
    IconText,
    LoadingIndicator,
    SnackbarPrompt,
  },
  mounted() {
    this.initGooglePlaces();
  },
  computed: {
    ...mapGetters(MODULES.PAYMENT, {
      orderSummary: "getOrderSummary",
    }),
    ...mapGetters(MODULES.PLAN, {
      allPlans: "getAllPlans",
    }),
    ...mapGetters(MODULES.COUNTRY, {
      countries: "getCountries",
      states: "getCountryStates",
    }),
  },
  methods: {
    getPhobia() {
      const { category_id } = this.formData;

      if (category_id.value === -1) {
        return {
          id: -1,
          image: "",
          phobia: "",
          title: "",
          description: "",
          tagline: "",
          statement: {
            description: "",
            title: "",
          },
        };
      }

      return getCategoryById(category_id.value);
    },
    getTitle() {
      const search = location.hash.replace("#/", "");

      if (search) {
        const obj = queryString.parse(search);

        if (obj["name"]) {
          return `${obj["name"]}, start your subscription`;
        }
      }

      return "Start your subscription";
    },
    updateForm(key, value, touched = false) {
      const { updateFormState } = this;

      updateFormState(PAYMENT_FORMS.partner, key, value, touched);
    },
    updateCountryField(event) {
      const { updateForm } = this;
      const { dispatch } = this.$store;

      if (event) {
        event.preventDefault();
      }

      const countryObj = this.countries.find(
        (country) => event.target.value === country.value
      );

      dispatch(`${MODULES.COUNTRY}/getCountryStatesById`, countryObj.id).then(
        () => {
          updateForm("countryId", countryObj.id, true);
          updateForm("countryAlpha2", event.target.value, true);
          updateForm("stateId", "", true); // Clear the stateId field
        }
      );
    },
    getSelectedPlan(planId) {
      return this.allPlans.find((p) => Number(p.id) === Number(planId));
    },
    initGooglePlaces() {
      this.googleService = new GooglePlaces();
      this.googleService.init();

      this.autoComplete = new this.googleService.service.Autocomplete(
        this.$refs.streetAddressLookUp,
        {
          types: ["geocode"],
        }
      );
    },
    autoCompleteService() {
      this.autoComplete.addListener("place_changed", () => {
        const results = fillInAddress(this.autoComplete);

        this.autoCompleteForm(results);
      });
    },
    autoCompleteForm(results) {
      const blackListedKeys = ["country", "name", "stateId", "email"];
      const keys = Object.keys(this.formData);

      const properties = (validator, key) =>
        Object.prototype.hasOwnProperty.call(validator, key);

      keys.forEach((key) => {
        if (properties(this.validator, key) && !blackListedKeys.includes(key)) {
          const replaceLocality = this.shouldReplaceLocality(key, results);

          this.updateForm(
            key,
            replaceLocality ? results.sublocality : results[key],
            false
          );
        }
      });

      this.updateCountryFromGoogle(results.countryAlpha, results?.state);
    },
    shouldReplaceLocality(key, results) {
      return key === "locality" && !results[key] && results.sublocality;
    },
    updateCountryFromGoogle(googleCountryAlpha, googleCountryState) {
      const findCountry = this.countries.find(
        (country) => googleCountryAlpha === country.value
      );

      this.updateForm("countryId", findCountry.id, false);
      this.updateForm("countryAlpha2", findCountry.value, false);

      this.getCountryStates(findCountry.id, googleCountryState);
    },
    getCountryStates(id, googleCountryState) {
      const { dispatch } = this.$store;

      dispatch(`${MODULES.COUNTRY}/getCountryStatesById`, id).then(() => {
        this.updateFormState(PAYMENT_FORMS.partner, "stateId", "", false);

        this.updateCountryStateFromGoogle(googleCountryState);
      });
    },
    // use regex to remove special characters, accents and put in lowercase
    updateCountryStateFromGoogle(googleCountryState) {
      if (googleCountryState) {
        const normalizedGoogleCountryState = googleCountryState
          .normalize("NFD")
          .replace(/[\u0300-\u036f]/g, "");
        const state = this.states.find(
          (cstate) =>
            cstate.label
              .normalize("NFD")
              .replace(/[\u0300-\u036f]/g, "")
              .toLowerCase() === normalizedGoogleCountryState.toLowerCase()
        );
        if (state) {
          this.updateForm("stateId", state.value.toString(), true);
        }
      } else {
        this.updateForm("stateId", "", false);
      }
    },
    formIsValid() {
      let keys = Object.keys(this.formData);

      const properties = (validator, key) =>
        Object.prototype.hasOwnProperty.call(validator, key);

      const formIsValid = !keys.some((key) => {
        if (properties(this.validator, key)) {
          if (key === "stateId" && this.countryHasNoStates()) {
            return false;
          }

          return !this.validator[key](this.formData[key].value).valid;
        }

        return false;
      });

      return formIsValid;
    },
    countryHasNoStates() {
      const { country } = this.formData;

      if (country.value && this.countryStates.length < 1) {
        return true;
      }

      return false;
    },
  },
};
</script>
